import { useState, useCallback } from "react";
import { useSession } from "@clerk/clerk-react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { toast } from "react-toastify";
import { MagnifyingGlass, CaretDown, ArrowRight } from "@phosphor-icons/react";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "components/ui/table";
import { Button } from "components/ui/button";
import { formatCurrency } from "shared/utils/currency";

interface OrphanedRow {
  odooId: number;
  documentNumber: string;
  excl: number;
  incl: number;
}
interface MismatchRow {
  odooId: number;
  documentNumber: string;
  yallyhubExcl: number;
  yallyhubIncl: number;
  odooAmount: number;
  delta: number;
}
interface MoveTypeRow {
  moveType: string;
  amount: number;
  net: number;
  count: number;
}
interface AccountRow {
  id: number;
  code: string;
  name: string;
  company: string;
}
interface DebugResult {
  buildingId: string;
  odooTotalAnalytic: number;
  odooTotalBuildingOnly: number;
  odooTotalUnitsOnly: number;
  odooTotalInclMemoriaal: number;
  odooTotalReportMethod: number;
  odooReportTotal: number;
  odooReportTotalExclMemoriaal: number;
  memoriaalNet: number;
  refundAbsTotal: number;
  renovationAccountCount: number;
  renovationAccounts: AccountRow[];
  odooByMoveType: MoveTypeRow[];
  yallyhub: { importedExcl: number; importedIncl: number; importedCount: number };
  teImporteren: number;
  teImporterenCount: number;
  presentImportedAnalytic: number;
  gapExcl: number;
  gapIncl: number;
  explainedBy: {
    orphanedExcl: number;
    orphanedIncl: number;
    amountMismatchDelta: number;
    basisExclMinusIncl: number;
    residualExcl: number;
  };
  orphanedCount: number;
  mismatchCount: number;
  orphaned: OrphanedRow[];
  mismatch: MismatchRow[];
}

interface Props {
  buildingId: string;
  /**
   * Optioneel: toon de probleemfacturen in de documententabel hieronder. Wordt
   * aangeroepen met de Odoo-status-filter die toegepast moet worden.
   */
  onShowInTable?: (status: "missing" | "mismatch") => void;
}

const MOVE_TYPE_LABELS: Record<string, string> = {
  in_invoice: "Leveranciersfactuur",
  in_refund: "Creditnota leverancier",
  entry: "Memoriaal / diverse boeking",
  out_invoice: "Klantfactuur",
  out_refund: "Creditnota klant",
  onbekend: "Onbekend",
};

/** Eén regel: label links, bedrag rechts (tabular). */
const Line = ({
  label,
  value,
  bold,
  muted,
}: {
  label: string;
  value: string;
  bold?: boolean;
  muted?: boolean;
}) => (
  <div className="flex items-baseline justify-between gap-4 py-1">
    <span
      className={
        muted
          ? "text-neutral-secondary-mid"
          : bold
          ? "font-semibold text-neutral-primary"
          : "text-neutral-primary"
      }
    >
      {label}
    </span>
    <span
      className={`tabular-nums ${
        bold ? "font-semibold text-neutral-primary" : "text-neutral-primary"
      }`}
    >
      {value}
    </span>
  </div>
);

/**
 * Reconciliatie met Odoo (per gebouw). Eén sluitend vergelijkingsblok:
 *
 *   Odoo kostenplaatsboeking
 *   In YallyHub (geïmporteerd + te importeren)
 *   = Verschil, opgesplitst in exact 2 oorzaken:
 *       · facturen niet meer in Odoo
 *       · facturen met afwijkend bedrag (gesplitst over kostenplaatsen)
 *
 * De ruwe cijfers staan achter "Technische details".
 */
export const ReconciliationDebug = ({ buildingId, onShowInTable }: Props) => {
  const { session } = useSession();
  const [data, setData] = useState<DebugResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  const run = useCallback(async () => {
    if (!session) return;
    setIsLoading(true);
    try {
      const response = await fetchWithAuth(
        `${API_URL}/renovation/reconciliation-debug?buildingId=${encodeURIComponent(
          buildingId,
        )}`,
        { method: "GET" },
        session,
      );
      if (!response.ok) {
        const text = await response.text().catch(() => response.statusText);
        throw new Error(`${response.status}: ${text}`);
      }
      setData(await response.json());
    } catch (error) {
      console.error("[ReconciliationDebug] error:", error);
      toast.error("Reconciliatie nakijken mislukt");
    } finally {
      setIsLoading(false);
    }
  }, [session, buildingId]);

  // Afgeleide, sluitende cijfers (alles excl. btw).
  const odoo = data?.odooTotalAnalytic ?? 0;
  const imported = data?.yallyhub.importedExcl ?? 0;
  const teImporteren = data?.teImporteren ?? 0;
  const yallyhubTotal = imported + teImporteren;
  const verschil = yallyhubTotal - odoo;
  const orphaned = data?.explainedBy.orphanedExcl ?? 0;
  const mismatch = data?.explainedBy.amountMismatchDelta ?? 0;
  const residu = data?.explainedBy.residualExcl ?? 0;
  const sluitAan = Math.abs(verschil) < 1;

  return (
    <div className="px-6 pt-1 pb-3">
      <div className="rounded-lg border border-neutral-200 bg-white p-4">
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm font-semibold text-neutral-primary">
            Reconciliatie met Odoo
            {/* Zelfde schrijfwijze als de kaarten hierboven: eerst "excl. btw", dan het
                gebouw tussen haakjes. */}
            <span className="text-xs font-normal text-neutral-secondary-mid">
              {" "}
              · excl. btw ({buildingId ? `gebouw ${buildingId}` : "alle gebouwen"})
            </span>
          </span>
          <Button variant="outline" size="sm" onClick={run} disabled={isLoading}>
            <MagnifyingGlass
              size={14}
              className={isLoading ? "animate-spin" : ""}
            />
            {isLoading ? "Bezig…" : "Nakijken"}
          </Button>
        </div>

        {data && (
          <div className="mt-4 flex flex-col gap-4 text-sm">
            {/* Hét vergelijkingsblok */}
            <div className="rounded-md border border-neutral-200">
              <div className="border-b border-neutral-200 px-4 py-2">
                <Line
                  label="Odoo kostenplaatsboeking"
                  value={formatCurrency(odoo)}
                  bold
                />
              </div>

              <div className="px-4 py-2">
                <div className="mb-1 text-xs font-medium uppercase tracking-wide text-neutral-secondary-mid">
                  In YallyHub
                </div>
                <Line
                  label={`Reeds geïmporteerd · ${data.yallyhub.importedCount} fact.`}
                  value={formatCurrency(imported)}
                  muted
                />
                <Line
                  label={`Nog te importeren · ${data.teImporterenCount} fact.`}
                  value={formatCurrency(teImporteren)}
                  muted
                />
                <div className="mt-1 border-t border-dashed border-neutral-200 pt-1">
                  <Line
                    label="Totaal YallyHub"
                    value={formatCurrency(yallyhubTotal)}
                    bold
                  />
                </div>
              </div>

              <div
                className={`border-t px-4 py-3 ${
                  sluitAan
                    ? "border-neutral-200 bg-moss-50"
                    : "border-neutral-200 bg-amber-50"
                }`}
              >
                <Line
                  label="Verschil (YallyHub − Odoo)"
                  value={formatCurrency(verschil)}
                  bold
                />

                {sluitAan ? (
                  <div className="mt-1 text-xs text-moss-700">
                    ✓ YallyHub en Odoo sluiten aan.
                  </div>
                ) : (
                  <div className="mt-2 flex flex-col gap-2">
                    {/* Oorzaak 1: niet meer in Odoo */}
                    <div className="flex items-center justify-between gap-3 rounded border border-orange-200 bg-white px-3 py-2">
                      <div className="min-w-0">
                        <div className="text-xs font-medium text-orange-800">
                          {data.orphanedCount} fact. niet meer in Odoo
                        </div>
                        <div className="text-[11px] text-neutral-secondary-mid">
                          Boeking verwijderd/gewijzigd in Odoo sinds import
                        </div>
                      </div>
                      <div className="flex items-center gap-2 whitespace-nowrap">
                        <span className="text-xs font-semibold tabular-nums text-neutral-primary">
                          {formatCurrency(orphaned)}
                        </span>
                        {onShowInTable && data.orphanedCount > 0 && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-6 px-2 text-xs"
                            onClick={() => onShowInTable("missing")}
                          >
                            Bekijk
                            <ArrowRight size={12} />
                          </Button>
                        )}
                      </div>
                    </div>

                    {/* Oorzaak 2: bedrag wijkt af / gesplitst */}
                    <div className="flex items-center justify-between gap-3 rounded border border-amber-200 bg-white px-3 py-2">
                      <div className="min-w-0">
                        <div className="text-xs font-medium text-amber-800">
                          {data.mismatchCount} fact. bedrag wijkt af
                        </div>
                        <div className="text-[11px] text-neutral-secondary-mid">
                          Gesplitst over kostenplaatsen — YallyHub nam 100%, Odoo
                          enkel het deel van dit gebouw
                        </div>
                      </div>
                      <div className="flex items-center gap-2 whitespace-nowrap">
                        <span className="text-xs font-semibold tabular-nums text-neutral-primary">
                          {formatCurrency(mismatch)}
                        </span>
                        {onShowInTable && data.mismatchCount > 0 && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-6 px-2 text-xs"
                            onClick={() => onShowInTable("mismatch")}
                          >
                            Bekijk
                            <ArrowRight size={12} />
                          </Button>
                        )}
                      </div>
                    </div>

                    {Math.abs(residu) >= 1 && (
                      <Line
                        label="Onverklaard residu"
                        value={formatCurrency(residu)}
                        muted
                      />
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Technische details — standaard ingeklapt */}
            <div>
              <button
                type="button"
                onClick={() => setShowDetails((v) => !v)}
                className="flex items-center gap-1 text-xs font-medium text-neutral-secondary-mid hover:text-neutral-primary"
              >
                <CaretDown
                  size={12}
                  className={`transition-transform ${
                    showDetails ? "rotate-0" : "-rotate-90"
                  }`}
                />
                Technische details
              </button>

              {showDetails && (
                <div className="mt-3 flex flex-col gap-4">
                  <div className="grid grid-cols-1 gap-x-8 gap-y-0.5 text-xs md:grid-cols-2">
                    <Line
                      label="Odoo — alleen gebouw-kostenplaats"
                      value={formatCurrency(data.odooTotalBuildingOnly)}
                      muted
                    />
                    <Line
                      label="Odoo — units van dit gebouw"
                      value={formatCurrency(data.odooTotalUnitsOnly)}
                      muted
                    />
                    <Line
                      label="Odoo INCL. memoriaal (jouw kostenplaatsboeking)"
                      value={formatCurrency(data.odooTotalInclMemoriaal)}
                      muted
                    />
                    <Line
                      label="Waarvan memoriaal (door YallyHub uitgesloten)"
                      value={formatCurrency(
                        data.odooTotalInclMemoriaal - data.odooTotalAnalytic,
                      )}
                      muted
                    />
                    <Line
                      label="YallyHub geïmporteerd · incl-VAT-basis"
                      value={formatCurrency(data.yallyhub.importedIncl)}
                      muted
                    />
                    <Line
                      label="Verschil op incl-VAT-basis"
                      value={formatCurrency(data.gapIncl)}
                      muted
                    />
                  </div>

                  {data.odooByMoveType.length > 0 && (
                    <div>
                      <div className="mb-1 text-xs font-semibold text-neutral-primary">
                        Odoo-totaal per boekingstype (incl. memoriaal)
                      </div>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Boekingstype</TableHead>
                            <TableHead className="text-right">Aantal</TableHead>
                            <TableHead className="text-right">
                              Absoluut
                            </TableHead>
                            <TableHead className="text-right">
                              Netto (Odoo)
                            </TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {data.odooByMoveType.map((t) => (
                            <TableRow key={t.moveType}>
                              <TableCell>
                                {MOVE_TYPE_LABELS[t.moveType] ?? t.moveType}
                              </TableCell>
                              <TableCell className="text-right tabular-nums">
                                {t.count}
                              </TableCell>
                              <TableCell className="text-right tabular-nums">
                                {formatCurrency(t.amount)}
                              </TableCell>
                              <TableCell className="text-right tabular-nums">
                                {formatCurrency(t.net)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}

                  {data.mismatch.length > 0 && (
                    <div>
                      <div className="mb-1 text-xs font-semibold text-amber-700">
                        Bedrag wijkt af ({data.mismatch.length})
                      </div>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Factuur</TableHead>
                            <TableHead className="text-right">YallyHub</TableHead>
                            <TableHead className="text-right">Odoo</TableHead>
                            <TableHead className="text-right">Verschil</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {data.mismatch.map((m) => (
                            <TableRow key={m.odooId}>
                              <TableCell>{m.documentNumber}</TableCell>
                              <TableCell className="text-right tabular-nums">
                                {formatCurrency(m.yallyhubExcl)}
                              </TableCell>
                              <TableCell className="text-right tabular-nums">
                                {formatCurrency(m.odooAmount)}
                              </TableCell>
                              <TableCell className="text-right tabular-nums">
                                {formatCurrency(m.delta)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}

                  {data.orphaned.length > 0 && (
                    <div>
                      <div className="mb-1 text-xs font-semibold text-orange-700">
                        Niet meer in Odoo ({data.orphaned.length})
                      </div>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Factuur</TableHead>
                            <TableHead className="text-right">
                              YallyHub (excl)
                            </TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {data.orphaned.map((o) => (
                            <TableRow key={o.odooId}>
                              <TableCell>{o.documentNumber}</TableCell>
                              <TableCell className="text-right tabular-nums">
                                {formatCurrency(o.excl)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReconciliationDebug;
